import ee from '../../services/earth-engine';

/**
 * @function addTideBandToImage
 * @description
 * Adds a tide level band to the input image based on the closest tide gauge measurement in time.
 * The tide data is provided as an array of [datetimeStr, value] pairs. The function finds the tide measurement closest in time to the image's timestamp and adds it as a new band ('tideLevel') to the image. It also sets metadata properties for the tide value and the dates of the image and gauge measurement.
 * The function:
 * 1. Parses the tide data array into an EE FeatureCollection, converting datetime strings
 *    to milliseconds and applying the timezone offset.
 * 2. Optionally filters gauge records to a maximum time difference from the image timestamp
 *    (controlled by tideConfig.maxDiffMin).
 * 3. Selects the gauge record with the smallest time difference from the image timestamp.
 * 4. Adds the matched tide level as a constant band ('tideLevel') to the image.
 * 5. Sets image metadata: tideValue, dateGauge, dateImage, tide_match_ok, tide_rows.
 * If no tide data is provided or no records fall within the allowed time window,
 * the function returns the original image unchanged (no tide band added).
 * @param {ee.Image} image - The bathymetric image to which the tide band will be added.
 * @param {Array<Array<string|number>>} tideData - An array of [datetimeStr, value] pairs from the tide CSV.
 * @param {ee.Number} timezoneOffset -Timezone offset in hours to apply to gauge datetimes (e.g. -3 for UTC-3).
 * @param {ee.Image} sourceImage -Image used only to read system:time_start. For composites, pass a synthetic image with the timestamp set manually.
 * @param {Object} tideConfig=- Configuration object with the following fields:
 * tideConfig.datetimeFormat='yyyy-MM-dd HH:mm:ss',
 * tideConfig.maxDiffMin=null ->  Maximum allowed time difference in minutes between image and gauge. If null or 0, no filtering is applied.]
 * **Called by:
 * @see {ProcessingPage}
 * @see {computeMetrics}
 * @memberof module:Bathymetry
 * @example
 * -> Composite mode — pass synthetic image with timestamp
 * const imageTimestamp = ee.Image(1).set('system:time_start', ee.Date('2023-03-20').millis());
 * const imageWithTide = addTideBandToImage(
      ...,
 *   imageTimestamp,
 *   data.tideConfig,
 * );
 * -> Single image mode — pass the original image as sourceImage
 * const imageWithTide = addTideBandToImage(
 *    ...,
 *   cleanImage,
 *   data.tideConfig,
 * );
 * @returns {ee.Image} The input image with an added 'tideLevel' float band, and the following properties set:
 * - tideValue {ee.Number}: matched tide level in the original gauge units
 * - dateGauge {ee.String}: formatted datetime of the matched gauge record, or 'NO_MATCH'
 * - dateImage {ee.String}: formatted datetime of the image timestamp, or 'NO_IMAGE_DATE'
 * - tide_match_ok {ee.Number}: 1 if a match was found, 0 otherwise
 * - tide_rows {number}: total number of valid gauge rows parsed from tideData
 */
export const addTideBandToImage = (image, tideData, timezoneOffset, sourceImage, tideConfig) => {
  try {
    const cfg = tideConfig || {};
    const datetimeFormat = cfg.datetimeFormat || 'yyyy-MM-dd HH:mm:ss';
    const maxDiffMin = cfg.maxDiffMin ?? null;
    const compositeReducerName = (cfg.compositeReducer || '').toLowerCase();
    const useCompositeReducer = !!compositeReducerName;

    if (!tideData?.length) {
      console.warn('No tide data provided');
      return image;
    }

    const timeStart = ee.Image(sourceImage).get('system:time_start');

    const imgTimeMs = ee.Number(ee.Algorithms.If(ee.Algorithms.IsEqual(timeStart, null), 0, timeStart));
    const hasImageTime = ee.Number(imgTimeMs).gt(0);

    const tideFeatures = tideData
      .filter(([datetimeStr, value]) => {
        return (
          datetimeStr &&
          datetimeStr.trim() !== '' &&
          value !== null &&
          value !== undefined &&
          !Number.isNaN(Number(value))
        );
      })
      .map(([datetimeStr, value]) =>
        ee.Feature(null, {
          tideDateTime: String(datetimeStr).trim(),
          tideValue: Number(value),
        }),
      );

    if (!tideFeatures.length) {
      console.warn('No valid tide rows found');
      return image;
    }

    let tideGauge = ee.FeatureCollection(tideFeatures).map((feature) => {
      const dt = ee.Date.parse(datetimeFormat, ee.String(feature.get('tideDateTime'))).advance(timezoneOffset, 'hour');
      return feature.set('tideTimeMs', dt.millis());
    });

    if (maxDiffMin !== null && maxDiffMin > 0) {
      const maxDiffMs = ee.Number(maxDiffMin).multiply(60 * 1000);
      tideGauge = tideGauge
        .filter(ee.Filter.gte('tideTimeMs', imgTimeMs.subtract(maxDiffMs)))
        .filter(ee.Filter.lte('tideTimeMs', imgTimeMs.add(maxDiffMs)));
    }

    const withDiff = tideGauge.map((feature) => {
      const diff = ee
        .Number(feature.get('tideTimeMs'))
        .subtract(imgTimeMs)
        .abs()
        .divide(60 * 1000);
      return feature.set('timeDiffMin', diff);
    });

    const n = withDiff.size();

    const best = ee.Feature(
      ee.Algorithms.If(
        n.gt(0),
        withDiff.sort('timeDiffMin').first(),
        ee.Feature(null, { tideValue: 0, tideTimeMs: 0 }),
      ),
    );
    let tideValue;

    if (useCompositeReducer) {
      const aggregateMap = {
        min: (fc) => fc.aggregate_min('tideValue'),
        max: (fc) => fc.aggregate_max('tideValue'),
        mean: (fc) => fc.aggregate_mean('tideValue'),
        median: (fc) => fc.aggregate_array('tideValue').reduce(ee.Reducer.median()),
      };
      const aggregateFn = aggregateMap[compositeReducerName];
      if (!aggregateFn) throw new Error(`compositeReducer inválido: ${compositeReducerName}`);
      tideValue = ee.Number(ee.Algorithms.If(n.gt(0), aggregateFn(withDiff), 0));
    } else {
      tideValue = ee.Number(ee.Algorithms.If(n.gt(0), best.get('tideValue'), 0));
    }

    const tideBand = ee.Image.constant(tideValue).rename('tideLevel').toFloat();

    const gaugeDate = ee.String(
      ee.Algorithms.If(n.gt(0), ee.Date(best.get('tideTimeMs')).format('yyyy-MM-dd HH:mm'), 'NO_MATCH'),
    );
    const imageDate = ee.String(
      ee.Algorithms.If(hasImageTime, ee.Date(imgTimeMs).format('yyyy-MM-dd HH:mm'), 'NO_IMAGE_DATE'),
    );

    return image
      .addBands(tideBand, null, true)
      .set('tideValue', tideValue)
      .set('dateGauge', gaugeDate)
      .set('dateImage', imageDate)
      .set('tide_match_ok', n.gt(0))
      .set('tide_rows', tideFeatures.length);
  } catch (err) {
    console.error('addTideBandToImage error', err);
    return image;
  }
};
